import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import {
  CASH_MOVEMENT_KINDS,
  CASH_MOVEMENT_KIND_LABEL,
  CASH_MOVEMENT_KIND_LABEL_KEY,
  formatMinor,
  useCashMovements,
  type CashMovementKind,
} from '@/hooks/use-shifts';
import { useT } from '@/i18n/use-t';

/**
 * Catalogue key plus its baked English, per movement kind. The whole set is
 * declared here so the keys stay readable from this file even though the filter
 * row resolves them by index.
 */
const KIND_LABELS: Record<CashMovementKind, readonly [key: string, en: string]> = {
  drop: ['shifts.movements.kind.drop', 'Drops'],
  pickup: ['shifts.movements.kind.pickup', 'Pickups'],
  bank_deposit: ['shifts.movements.kind.bankDeposit', 'Bank deposits'],
  change_order: ['shifts.movements.kind.changeOrder', 'Change orders'],
  paid_in: ['shifts.movements.kind.paidIn', 'Cash in'],
  paid_out: ['shifts.movements.kind.paidOut', 'Cash out'],
  change_in: ['shifts.movements.kind.changeIn', 'Change in'],
  change_out: ['shifts.movements.kind.changeOut', 'Change out'],
  revenue_out: ['shifts.movements.kind.revenueOut', 'Revenue out'],
};

export function CashMovementsPage() {
  const t = useT('admin_ui');
  const [kind, setKind] = useState<CashMovementKind | 'all'>('all');
  const { data, isLoading } = useCashMovements({
    kind: kind === 'all' ? undefined : kind,
  });

  const kindFilters: { label: string; value: CashMovementKind | 'all' }[] = [
    { label: t('shifts.movements.filter.all', 'All'), value: 'all' },
    ...CASH_MOVEMENT_KINDS.map((value) => ({
      value,
      label: t(KIND_LABELS[value][0], KIND_LABELS[value][1]),
    })),
  ];

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">{t('shifts.movements.title', 'Cash movements')}</h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'shifts.movements.intro',
            'All cash entering or leaving drawers across shifts. Authored on terminals.',
          )}
        </p>
      </header>

      <div className="flex items-center gap-1 rounded-md border border-border bg-card p-1 text-sm">
        {kindFilters.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setKind(opt.value)}
            className={
              kind === opt.value
                ? 'rounded bg-primary px-3 py-1 text-primary-foreground'
                : 'rounded px-3 py-1 text-muted-foreground hover:bg-secondary'
            }
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="rounded-md border border-border bg-card">
        <Table>
          <THead>
            <TR>
              <TH>{t('shifts.field.when', 'When')}</TH>
              <TH>{t('shifts.field.shift', 'Shift')}</TH>
              <TH>{t('shifts.field.kind', 'Kind')}</TH>
              <TH className="text-right">{t('shifts.field.amount', 'Amount')}</TH>
              <TH>{t('shifts.field.reason', 'Reason')}</TH>
              <TH>{t('shifts.field.notes', 'Notes')}</TH>
            </TR>
          </THead>
          <TBody>
            {isLoading && (
              <TR>
                <TD colSpan={6} className="py-8 text-center text-muted-foreground">
                  {t('common.loading', 'Loading…')}
                </TD>
              </TR>
            )}
            {data?.movements.length === 0 && (
              <TR>
                <TD colSpan={6} className="py-8 text-center text-muted-foreground">
                  {t('shifts.movements.empty', 'No cash movements yet.')}
                </TD>
              </TR>
            )}
            {data?.movements.map((m) => (
              <TR key={m.id}>
                <TD className="text-xs">{formatDateTime(m.occurredAt)}</TD>
                <TD className="font-mono text-xs">{m.shiftId.slice(0, 8)}…</TD>
                <TD>
                  <Badge variant="outline">
                    {t(CASH_MOVEMENT_KIND_LABEL_KEY[m.kind], CASH_MOVEMENT_KIND_LABEL[m.kind])}
                  </Badge>
                </TD>
                <TD className="text-right tabular-nums">
                  {formatMinor(m.amountMinor, m.currencyCode)}
                </TD>
                <TD className="text-sm">{m.reasonCode}</TD>
                <TD className="text-xs text-muted-foreground">{m.notes ?? '—'}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </div>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}
